import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { LiveMark } from '../components/ui/LiveMark';
import { Wordmark } from '../components/ui/Wordmark';
import { api, ApiError, prefetchHistory, type Me } from './api';

interface SessionState {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<Me | null>;
  setMe: (me: Me | null) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { me: next } = await api.me();
      setMe(next);
      return next;
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) console.error(err);
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial session probe. The first history page is prefetched in parallel so the shell
  // opens with the conversation ready instead of a second round trip.
  useEffect(() => {
    let cancelled = false;
    prefetchHistory();
    api
      .me()
      .then(({ me: next }) => {
        if (!cancelled) setMe(next);
      })
      .catch((err: unknown) => {
        if (!(err instanceof ApiError && err.status === 401)) console.error(err);
        if (!cancelled) setMe(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      setMe(null);
    }
  }, []);

  const value = useMemo(
    () => ({ me, loading, refresh, setMe, signOut }),
    [me, loading, refresh, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/** The session when a provider is present (components that also render standalone, e.g. in tests). */
export function useMaybeSession(): SessionState | undefined {
  return useContext(SessionContext);
}

/** The app shell while the session resolves: never a blank page, the LiveMark thinking. */
export function ShellLoading() {
  const { t } = useTranslation();
  return (
    <div className="vv-root bg-canvas text-ink" aria-busy>
      <header className="flex shrink-0 items-center gap-3 border-b border-line bg-surface px-4 pt-safe pb-3 pt-3">
        <Wordmark />
        <LiveMark state="thinking" size={16} title={t('common.loading')} />
      </header>
      <div className="flex flex-1 items-center justify-center" role="status">
        <LiveMark state="thinking" size={20} title={t('common.loading')} />
      </div>
    </div>
  );
}

/** Redirects to /signin without a session, and to /onboarding until the profile is complete. */
export function RequireAuth({
  children,
  allowUnonboarded = false,
}: {
  children: ReactNode;
  allowUnonboarded?: boolean;
}) {
  const { me, loading } = useSession();
  const location = useLocation();
  if (loading) return <ShellLoading />;
  if (!me) return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  if (!me.onboarded && !allowUnonboarded) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}
